import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Download, MessageCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

function inr(n) {
  return "₹" + (Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function ClientLedger({ clientId, clientName, clientMobile, clientEmail }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/clients/${clientId}/ledger`).then(({ data }) => setData(data)).catch(() => {});
  }, [clientId]);

  function buildStatementMessage() {
    if (!data) return "";
    const overdue = data.outstanding > 0;
    const lines = [
      `Dear ${clientName},`,
      "",
      `Statement of your account:`,
      `• Total billed: ${inr(data.total_billed)}`,
      `• Total received: ${inr(data.total_received)}`,
      `• Outstanding: ${inr(data.outstanding)}`,
      "",
      overdue ? "Kindly arrange for payment at your earliest convenience." : "Your account is up to date. Thank you!",
      "",
      "Regards,",
      "Team Vijay",
    ];
    return lines.join("\n");
  }

  async function shareWhatsApp() {
    if (!clientMobile) { toast.error("No mobile on file"); return; }
    const mob = clientMobile.replace(/[^0-9]/g, "");
    const num = mob.length === 10 ? "91" + mob : mob;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(buildStatementMessage())}`, "_blank");
  }

  if (!data) return <Card className="p-6 text-sm text-muted-foreground">Loading ledger…</Card>;

  return (
    <div className="space-y-4" data-testid="client-ledger">
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4 border-l-[3px] border-blue-500/60">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total Billed</div>
          <div className="font-mono font-semibold text-2xl mt-2 tabular-nums">{inr(data.total_billed)}</div>
        </Card>
        <Card className="p-4 border-l-[3px] border-emerald-500/60">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total Received</div>
          <div className="font-mono font-semibold text-2xl mt-2 tabular-nums">{inr(data.total_received)}</div>
        </Card>
        <Card className={`p-4 border-l-[3px] ${data.outstanding > 0 ? "border-red-500/60" : "border-slate-300"}`}>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Outstanding</div>
          <div className={`font-mono font-semibold text-2xl mt-2 tabular-nums ${data.outstanding > 0 ? "text-red-600" : ""}`}>{inr(data.outstanding)}</div>
        </Card>
      </div>

      {(data.outstanding > 0 || true) && (
        <div className="flex gap-2 flex-wrap">
          <Button
            data-testid="ledger-download-pdf-btn"
            variant="outline"
            asChild
          >
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/ledger.pdf`}
              target="_blank"
              rel="noreferrer"
            >
              <Download className="w-4 h-4 mr-1.5" /> Download PDF
            </a>
          </Button>
          {data.outstanding > 0 && clientMobile && (
            <Button data-testid="ledger-whatsapp-btn" onClick={shareWhatsApp} className="bg-emerald-600 hover:bg-emerald-700">
              <MessageCircle className="w-4 h-4 mr-1.5" /> Send statement via WhatsApp
            </Button>
          )}
          {data.outstanding > 0 && clientEmail && (
            <Button variant="outline" asChild>
              <a href={`mailto:${clientEmail}?subject=Statement of Account&body=${encodeURIComponent(buildStatementMessage())}`}>
                <Mail className="w-4 h-4 mr-1.5" /> Email statement
              </a>
            </Button>
          )}
        </div>
      )}

      <Card className="p-0">
        <Table className="stripe-hover">
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Date</TableHead>
              <TableHead className="w-24">Type</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-28 text-right">Debit</TableHead>
              <TableHead className="w-28 text-right">Credit</TableHead>
              <TableHead className="w-28 text-right">Balance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.entries.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-sm text-muted-foreground">No transactions yet.</TableCell></TableRow>}
            {data.entries.map((e) => (
              <TableRow key={`${e.type}-${e.invoice_id || ""}-${e.date}-${e.debit || 0}-${e.credit || 0}`}>
                <TableCell className="font-mono text-xs">{e.date ? format(parseISO(e.date), "dd MMM yy") : "—"}</TableCell>
                <TableCell>
                  <Badge variant={e.type === "invoice" ? "outline" : "default"} className={`text-[10px] ${e.type === "payment" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 border-0" : ""}`}>
                    {e.type === "invoice" ? "Invoice" : "Payment"}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs">
                  <span className="font-mono font-medium">{e.ref}</span>
                  <span className="text-muted-foreground ml-1">— {e.description}</span>
                </TableCell>
                <TableCell className="text-right font-mono text-xs tabular-nums">{e.debit ? inr(e.debit) : "—"}</TableCell>
                <TableCell className="text-right font-mono text-xs tabular-nums text-emerald-600">{e.credit ? inr(e.credit) : "—"}</TableCell>
                <TableCell className={`text-right font-mono text-xs tabular-nums font-semibold ${e.balance > 0 ? "text-red-600" : ""}`}>{inr(e.balance)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
